import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, Image, Download, Printer, MapPin, Package } from 'lucide-react';
import { useState, useEffect } from 'react';

import { CartItemRow } from '@/components/cart/CartItemRow';
import { CartBundleRow } from '@/components/cart/CartBundleRow';
import { CartPrintRow } from '@/components/cart/CartPrintRow';
import { ShippingAddressForm } from '@/components/cart/ShippingAddressForm';
import { CartSummary } from '@/components/cart/CartSummary';
import { logError } from '@/lib/logger';

import { API } from '@/lib/apiBase';

export default function Cart() {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const {
    items, printItems, bundleItems, shippingAddress,
    getBundleKey, clearCart, hasPrints
  } = useCart();
  const [loading, setLoading] = useState(false);
  const [siblingDiscount, setSiblingDiscount] = useState(null);

  // Live-preview the sibling discount whenever digital/print items change.
  // Bundles are skipped by the backend so we don't include them.
  useEffect(() => {
    if (items.length === 0 && printItems.length === 0) {
      setSiblingDiscount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/cart/discount-preview`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            photo_ids: items.map((i) => i.photo_id),
            print_items: printItems.map((p) => ({
              photo_id: p.photo_id,
              format_price: p.format_price,
              quantity: p.quantity,
            })),
          }),
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setSiblingDiscount(data.eligible ? data : null);
        }
      } catch (e) {
        logError('Cart.discountPreview', e);
      }
    })();
    return () => { cancelled = true; };
  }, [items, printItems, getAuthHeaders]);

  const handleCheckout = async () => {
    if (items.length === 0 && printItems.length === 0 && bundleItems.length === 0) {
      toast.error(t('cart.emptyCart'));
      return;
    }

    if (hasPrints()) {
      if (!shippingAddress.name || !shippingAddress.street || !shippingAddress.city || !shippingAddress.postal_code) {
        toast.error(t('prints.fillShipping'));
        return;
      }
    }

    setLoading(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const body = {
        origin_url: window.location.origin,
        photo_ids: bundleItems.length > 0 ? [] : items.map(item => item.photo_id),
        print_items: printItems.map(item => ({
          photo_id: item.photo_id,
          format_id: item.format_id,
          quantity: item.quantity
        })),
        shipping_address: hasPrints() ? shippingAddress : null,
        bundle_id: null,
        bundle_items: bundleItems.map(b => ({
          bundle_id: b.bundle_id,
          student_id: b.student_id || null
        }))
      };

      const response = await fetch(`${API}/checkout/create-session`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('Failed to create checkout session');

      const data = await response.json();
      if (data.free && data.order_id) {
        clearCart();
        window.location.href = `/checkout/success?order_id=${data.order_id}&free=1`;
        return;
      }
      window.location.href = data.checkout_url;
    } catch (error) {
      logError('Cart.Checkouterror', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const totalItems = items.length + printItems.length + bundleItems.length;

  if (totalItems === 0) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="cart-page">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('cart.title')}</h1>
        </div>
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('cart.emptyCart')}</h3>
          <p className="text-muted-foreground mb-6">{t('cart.emptyCartDesc')}</p>
          <Button asChild className="bg-primary hover:bg-primary/90">
            <Link to="/gallery">
              <Image className="w-4 h-4 mr-2" />
              {t('cart.browseGallery')}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="cart-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('cart.title')}</h1>
        <p className="text-muted-foreground">
          {totalItems} {totalItems === 1 ? t('cart.item') : t('cart.items')} {t('cart.itemsInCart')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {items.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Download className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                  {t('prints.digitalItems')} ({items.length})
                </h3>
              </div>
              <div className="space-y-3">
                {items.map((item) => <CartItemRow key={item.photo_id} item={item} />)}
              </div>
            </div>
          )}

          {bundleItems.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-green-500" />
                <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                  {t('bundles.photoBundle')} ({bundleItems.length})
                </h3>
              </div>
              <div className="space-y-3">
                {bundleItems.map((bi) => <CartBundleRow key={getBundleKey(bi)} bundle={bi} />)}
              </div>
            </div>
          )}

          {printItems.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Printer className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                  {t('prints.printItems')} ({printItems.length})
                </h3>
              </div>
              <div className="space-y-3">
                {printItems.map((item) => <CartPrintRow key={item.key} item={item} />)}
              </div>
            </div>
          )}

          {printItems.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                  {t('prints.shippingAddress')}
                </h3>
              </div>
              <ShippingAddressForm />
            </div>
          )}

          {totalItems > 1 && (
            <Button
              variant="ghost"
              onClick={() => { clearCart(); toast.info(t('cart.clearCart')); }}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('cart.clearCart')}
            </Button>
          )}
        </div>

        <div className="lg:col-span-1">
          <CartSummary onCheckout={handleCheckout} loading={loading} siblingDiscount={siblingDiscount} />
        </div>
      </div>
    </div>
  );
}
